import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useTenantContext } from '../context/TenantContext';
import { useMemo } from 'react';

interface PaymentProviderCountry {
  country_code: string;
  country_name: string;
  country_flag: string;
  dial_code: string;
}

interface PaymentProvider {
  id: string;
  name: string;
  description: string;
  logo_url: string;
  is_active: boolean;
  display_order: number;
  min_amount: number;
  countries?: PaymentProviderCountry[];
}

interface TenantPaymentConfig {
  provider_id: string;
  is_enabled: boolean;
  display_order: number;
}

/**
 * Hook pour récupérer les moyens de paiement avec la configuration spécifique au tenant.
 * Si le tenant a une config, on filtre et ordonne selon cette config.
 * Sinon, on retourne les providers globaux actifs.
 */
export function useTenantPaymentProviders() {
  const { tenant } = useTenantContext();
  const tenantId = tenant?.id ? parseInt(tenant.id, 10) : null;

  // Charger les providers globaux actifs
  const { data: globalProviders, isLoading: isLoadingProviders } = useQuery({
    queryKey: ['payment-providers'],
    queryFn: async () => {
      const { data: providers, error: providersError } = await supabase
        .from('payment_providers')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (providersError) throw providersError;

      // Charger les pays pour chaque provider
      const { data: countries, error: countriesError } = await supabase
        .from('payment_provider_countries')
        .select('*');

      if (countriesError) throw countriesError;

      // Associer les pays aux providers
      return (providers || []).map((provider: PaymentProvider) => ({
        ...provider,
        countries: (countries || []).filter(
          (c: any) => c.provider_id === provider.id
        ),
      }));
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Charger la config spécifique au tenant
  const { data: tenantConfig, isLoading: isLoadingConfig } = useQuery({
    queryKey: ['tenant-payment-config', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      const { data, error } = await supabase
        .from('tenant_payment_providers')
        .select('provider_id, is_enabled, display_order')
        .eq('tenant_id', tenantId);

      if (error) {
        console.error('Erreur lors du chargement de la config de paiement du tenant:', error);
        return [];
      }

      return data || [];
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  // Calculer les providers effectifs
  const effectiveProviders = useMemo(() => {
    if (!globalProviders) return [];

    // Si aucune config spécifique pour ce tenant, retourner les providers globaux
    if (!tenantConfig || tenantConfig.length === 0) {
      return globalProviders;
    }

    // Créer une map des providers activés pour ce tenant
    const enabledMap = new Map(
      tenantConfig
        .filter((cfg: TenantPaymentConfig) => cfg.is_enabled)
        .map((cfg: TenantPaymentConfig) => [cfg.provider_id, cfg])
    );

    // Filtrer les providers selon la config tenant
    const filtered = globalProviders.filter((p: PaymentProvider) =>
      enabledMap.has(p.id)
    );

    // Trier par display_order du tenant, puis par display_order global
    return filtered.sort((a: PaymentProvider, b: PaymentProvider) => {
      const cfgA = enabledMap.get(a.id) as TenantPaymentConfig;
      const cfgB = enabledMap.get(b.id) as TenantPaymentConfig;
      return (
        (cfgA?.display_order ?? 0) - (cfgB?.display_order ?? 0) ||
        (a.display_order ?? 0) - (b.display_order ?? 0)
      );
    });
  }, [globalProviders, tenantConfig]);

  return {
    data: effectiveProviders,
    isLoading: isLoadingProviders || isLoadingConfig,
    globalProviders,
    tenantConfig,
  };
}
